import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import createSlug from "../../createSlug";
const FirstSOVFullImage = "/images/imagePlaceholder.webp";

// Function to truncate content to 150 characters
const parseContent = (blogContent) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = blogContent;
  const content = tempDiv.textContent || tempDiv.innerText || "";

  // Split content into words
  const words = content.split(/\s+/); // Split by whitespace
  const wordLimit = 28; // Limit to 28 words

  // Truncate and join words
  return words.slice(0, wordLimit).join(" ") + (words.length > wordLimit ? "..." : "");
};

const BlogCard = ({ item }) => {
  const slug = useMemo(() => createSlug(item.blog_heading), [item.blog_heading]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const {
    blog_heading,
    coverImage,
    createdAt,
    blog_content = "",
    serial_number,
  } = item;

  // Memoized date formatting for better performance
  const formattedDate = useMemo(() => {
    return new Date(createdAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [createdAt]);

  // Memoized content truncation
  const content = useMemo(() => parseContent(blog_content), [blog_content]);

  return (
    <Link
      to={`/blogs/${serial_number}/${slug}`}
      className="group block w-full md:w-[100%] lg:w-[100%] cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <article className="bg-white rounded-xl overflow-hidden shadow-[rgba(0,0,0,0.08)_0px_4px_20px_0px] hover:shadow-[rgba(0,0,0,0.25)_0px_20px_40px_0px] transition-all duration-500 ease-out transform hover:scale-[1.02] hover:-translate-y-2 h-full flex flex-col">
        
        {/* Image Section with Enhanced Effects */}
        <div className="relative h-[18rem] w-full overflow-hidden">
          <div className="relative h-full w-full">
            <img
              loading="lazy"
              src={imageLoaded ? coverImage : FirstSOVFullImage}
              alt="Akiya 2.0 blogs"
              className={`object-cover w-full h-full transition-all duration-700 ease-out ${
                isHovered 
                  ? 'scale-110 brightness-110' 
                  : 'scale-100 brightness-100'
              }`}
              onLoad={() => setImageLoaded(true)}
            />
            
            {/* Loading shimmer effect */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]">
                <div className="absolute inset-0 bg-gray-200/50"></div>
              </div>
            )}
            
            {/* Hover overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent transition-all duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}></div>
            
            {/* Date badge with modern styling */}
            <div className={`absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-[#10c9c0] px-3 py-2 rounded-lg text-sm font-semibold shadow-lg transition-all duration-500 ${
              isHovered ? 'scale-105 bg-white' : 'scale-100'
            }`}>
              {formattedDate}
            </div>
          </div>
        </div>

        {/* Content Section with Enhanced Typography */}
        <div className="flex-1 flex flex-col p-6 space-y-4">
          {/* Blog Heading */}
          <h2 className={`text-xl font-bold leading-tight text-gray-900 line-clamp-2 transition-all duration-500 ${
            isHovered 
              ? 'text-[#10c9c0] transform translate-x-1' 
              : 'text-gray-900 transform translate-x-0'
          }`}>
            {blog_heading}
          </h2>
          
          {/* Blog Content */}
          <p className={`font-light text-md text-[#949995] leading-relaxed break-words overflow-hidden line-clamp-3 transition-all duration-500 delay-75 flex-1 ${
            isHovered 
              ? 'text-[#737873] transform translate-x-1' 
              : 'text-[#949995] transform translate-x-0'
          }`}>
            {content}
          </p>
          
          {/* Read More Indicator */}
          <div className={`flex items-center gap-2 text-[#10c9c0] font-semibold text-sm transition-all duration-300 ${
            isHovered ? 'translate-x-1' : 'translate-x-0'
          }`}>
            <span>Read More</span>
            <svg 
              className={`w-4 h-4 transition-all duration-300 ${
                isHovered ? 'translate-x-1' : 'translate-x-0'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
        
        {/* Subtle border accent */}
        <div className={`absolute inset-0 rounded-xl border-2 transition-all duration-500 pointer-events-none ${
          isHovered 
            ? 'border-[#10c9c0]/30' 
            : 'border-transparent'
        }`}></div>
        
        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] transition-all duration-500 ${
          isHovered ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
        } origin-left`}></div>
      </article>
      
      {/* Custom CSS for shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </Link>
  );
};

export default BlogCard;